import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useEffect } from 'react';

import { useSearchParams } from '@/lib/compat/navigation';
import { isNativeShell } from '@/lib/native/platform';
import { LoginContent } from '@/screens/login/LoginContent';

export const Route = createFileRoute('/_app/login/')({
  component: LoginRoute,
});

/**
 * Login entry. With no error/success query params it kicks off the backend
 * OIDC flow (`/auth/login`, a full navigation — not an SPA route). Otherwise it
 * renders the login surface with the error/success message.
 */
function LoginRoute() {
  const sp = useSearchParams();
  const error = sp.get('error') ?? undefined;
  const errorDescription = sp.get('error_description') ?? undefined;
  const registered = sp.get('registered') === 'true';
  const resetSuccess = sp.get('reset') === 'success';
  const shouldStartLogin = !error && !registered && !resetSuccess;

  useEffect(() => {
    if (!shouldStartLogin) return;
    if (isNativeShell()) {
      // Native: open the system-browser login (relative /auth/login would point
      // into the app bundle). Resolves out-of-band via the deep-link listener.
      void import('@/lib/native/auth')
        .then((m) => m.startNativeLogin())
        .catch((err) => console.error('[native-auth] login route start failed:', err));
    } else {
      window.location.assign('/auth/login');
    }
  }, [shouldStartLogin]);

  if (shouldStartLogin) return null;

  return (
    <Suspense>
      <LoginContent
        error={error}
        errorDescription={errorDescription}
        forgotUrl="/forgot-password"
        registered={registered}
        resetSuccess={resetSuccess}
      />
    </Suspense>
  );
}
